import type { GraphDiffDto } from '@process-hub/shared';

/** What changed between two versions, as an approver will read it (BUILD.md §7). */
export function Changes({ diff, against }: { diff: GraphDiffDto; against: string }) {
  const total = diff.added.length + diff.removed.length + diff.changed.length + diff.flow.added.length + diff.flow.removed.length + diff.process.length + (diff.reordered ? 1 : 0);
  if (total === 0) return <div className="notice" style={{ margin: 18 }}>No changes since v{against}.</div>;
  return (
    <div className="changes">
      <div className="changes__summary">
        Since v{against}: <strong>{describeBump(diff.suggestedBump)}</strong>
        {diff.reasons.length > 0 && <span className="muted"> — {summarize(diff.reasons)}</span>}
      </div>
      {diff.process.map((p) => (
        <div key={p.field} className="changes__row">
          <span className="changes__kind changes__kind--changed">Process</span>
          <span>
            {p.field}: <s className="muted">{String(p.before ?? '—')}</s> → {String(p.after ?? '—')}
          </span>
        </div>
      ))}
      {diff.added.map((s) => (
        <div key={s.key} className="changes__row">
          <span className="changes__kind changes__kind--added">Added</span>
          <span>{s.name}</span>
        </div>
      ))}
      {diff.removed.map((s) => (
        <div key={s.key} className="changes__row">
          <span className="changes__kind changes__kind--removed">Removed</span>
          <span>{s.name}</span>
        </div>
      ))}
      {diff.changed.map((c) => (
        <div key={c.key} className="changes__row">
          <span className={`changes__kind changes__kind--${c.fields.some((f) => f.bump === 'major') ? 'major' : 'changed'}`}>Changed</span>
          <span>
            <strong>{c.name}</strong>
            <ul className="changes__fields">
              {c.fields.map((f) => (
                <li key={f.field}>
                  {label(f.field)}: <s className="muted">{show(f.before)}</s> → {show(f.after)}
                </li>
              ))}
            </ul>
          </span>
        </div>
      ))}
      {(diff.flow.added.length > 0 || diff.flow.removed.length > 0) && (
        <div className="changes__row">
          <span className="changes__kind changes__kind--changed">Flow</span>
          <span>
            <ul className="changes__fields">
              {diff.flow.removed.map((t, i) => (
                <li key={`r${i}`}>
                  <s className="muted">
                    {t.from} → {t.to}
                    {t.condition ? ` (${t.condition})` : ''}
                  </s>
                </li>
              ))}
              {diff.flow.added.map((t, i) => (
                <li key={`a${i}`}>
                  {t.from} → {t.to}
                  {t.condition ? ` (${t.condition})` : ''}
                  {t.isDefault ? ' · default' : ''}
                </li>
              ))}
            </ul>
          </span>
        </div>
      )}
      {diff.reordered && (
        <div className="changes__row">
          <span className="changes__kind changes__kind--changed">Order</span>
          <span>Steps were reordered.</span>
        </div>
      )}
    </div>
  );
}

export function describeBump(b: GraphDiffDto['suggestedBump']): string {
  switch (b) {
    case 'major':
      return 'a role or system changed — major version';
    case 'minor':
      return 'steps or flow changed — minor version';
    case 'patch':
      return 'wording or timing only — patch version';
    default:
      return 'no change';
  }
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  role: 'Role',
  performedBy: 'Performed by',
  stepType: 'Type',
  systems: 'Systems',
  touchMinutes: 'Touch minutes',
  waitMinutes: 'Wait minutes',
  notes: 'Notes',
  raci: 'RACI',
  requiresJudgment: 'Requires judgment',
  involvesNegotiation: 'Involves negotiation',
  affectsClientTrust: 'Affects client trust',
  automationSignals: 'Automation signals',
  whyNotAutomate: 'Why not automate',
  overrideReason: 'Override reason',
};
const label = (f: string) => FIELD_LABELS[f] ?? f;

function show(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' && x ? Object.values(x as Record<string, unknown>).join(' ') : String(x))).join(', ') || '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function summarize(reasons: string[]): string {
  const unique = [...new Set(reasons)];
  return unique.length > 3 ? `${unique.slice(0, 3).join('; ')}; and ${unique.length - 3} more` : unique.join('; ');
}
